import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Bell, X, Menu, UserCircle, ChevronDown } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import PerfilModal, { iniciaisDe } from '../shared/PerfilModal';

function timeAgo(date) {
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return 'agora';
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Header({ onToggle }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [userMenu, setUserMenu] = useState(false);
  const [perfilOpen, setPerfilOpen] = useState(false);
  const userRef = useRef(null);
  const [usuario, setUsuario] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null') || {}; } catch { return {}; }
  });
  const iniciais = iniciaisDe(usuario.nome);

  const { data } = useQuery({
    queryKey: ['notificacoes-web'],
    queryFn: () => api.get('/notificacoes').then(r => r.data),
    refetchInterval: 15000,
  });

  const notifs = data?.data || [];
  const naoLidas = data?.nao_lidas || 0;

  useEffect(() => {
    if (naoLidas > 0 && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const prevCount = useRef(0);
  useEffect(() => {
    if (naoLidas > prevCount.current && naoLidas > 0 && Notification.permission === 'granted') {
      const latest = notifs.find(n => !n.lido);
      if (latest) new Notification(latest.titulo, { body: latest.mensagem, icon: '/favicon.ico' });
    }
    prevCount.current = naoLidas;
  }, [naoLidas]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const marcarLidas = async () => {
    await api.post('/notificacoes/marcar-lidas');
    qc.invalidateQueries(['notificacoes-web']);
  };

  function logout() {
    localStorage.removeItem('token');
    navigate('/login');
  }

  return (
    <header className="bg-white shadow-sm px-4 py-4 flex items-center justify-between flex-shrink-0">
      <button
        onClick={onToggle}
        title="Alternar menu"
        className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded"
      >
        <Menu size={22} />
      </button>
      <div className="flex items-center gap-4">
        <div className="relative" ref={ref}>
          <button onClick={() => setOpen(!open)} className="relative text-gray-500 hover:text-gray-700">
            <Bell size={20} />
            {naoLidas > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{naoLidas > 9 ? '9+' : naoLidas}</span>
            )}
          </button>
          {open && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Notificações</span>
                {naoLidas > 0 && <button onClick={marcarLidas} className="text-xs text-blue-600 hover:underline">Marcar todas como lidas</button>}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-8">Nenhuma notificação</p>
                ) : notifs.slice(0, 20).map(n => (
                  <div key={n.id} className={`px-4 py-3 border-b border-gray-50 ${!n.lido ? 'bg-blue-50' : ''}`}>
                    <p className="text-sm font-medium text-gray-800">{n.titulo}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.mensagem}</p>
                    <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="relative" ref={userRef}>
          <button onClick={() => setUserMenu(v => !v)} className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
            {usuario.avatar_url
              ? <img src={usuario.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
              : <span className="w-8 h-8 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{iniciais}</span>}
            <span className="hidden sm:block max-w-[140px] truncate">{usuario.nome}</span>
            <ChevronDown size={14} className="text-gray-400" />
          </button>
          {userMenu && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <button onClick={() => { setUserMenu(false); setPerfilOpen(true); }} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  {usuario.avatar_url
                    ? <img src={usuario.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    : <span className="w-10 h-10 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center flex-shrink-0">{iniciais}</span>}
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{usuario.nome}</span>
                    <span className="block text-xs text-gray-500 truncate">{usuario.email}</span>
                    <span className="flex items-center gap-1 text-xs text-secondary mt-0.5"><UserCircle size={12} /> Meu perfil</span>
                  </span>
                </div>
              </button>
              <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-danger">
                <LogOut size={16} /> Sair
              </button>
            </div>
          )}
        </div>
        <PerfilModal isOpen={perfilOpen} onClose={() => setPerfilOpen(false)} onSalvo={(u) => setUsuario(p => ({ ...p, ...u }))} />
      </div>
    </header>
  );
}

// ── Helper: Converter VAPID key ───────────────────────────────────────────────
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

